// ---------------------------------------------------------------------------
// The tariff-card STATUS PILL — ONE badge answering ONE question: "can this
// tariff bill right now?"
//
// Design change from the owner (2026-08-06), on top of TV-4's chain-integrity
// work: the card already carried an Active/Inactive pill
// (TariffManagement.tsx, historically `tariff.is_active ? <Badge
// tone="success">Active</Badge> : <Badge tone="danger">Inactive</Badge>`).
// Live D1: all 46 `tariff_version` rows and 38 of 39 tariffs are
// `is_active = 1`, so that pill was green on nearly everything and had
// stopped carrying information — an operator learns to skip a signal that
// never fires. This module REPLACES what renders in that same slot rather
// than adding effectivity/chain-integrity as separate badges beside it.
//
// Composes two independently-tested modules. No new date arithmetic is
// introduced here — everything below is either a precedence DECISION or a
// direct pass-through of an already-computed value:
//   - tariffEffectivity.ts's summariseTariff   → is something in force today?
//   - tariffChainIntegrity.ts's summariseChainIntegrity → does the chain
//     itself have a hole?
// plus the TARIFF row's own `is_active` (soft-delete via the "Delete"
// button), which is a DIFFERENT flag from any VERSION's `is_active` — a
// deleted tariff answers "no" regardless of what its dates say.
//
// ─────────────────────────────────────────────────────────────────────────
// PRECEDENCE (highest wins) — decided and stated, per the brief:
// ─────────────────────────────────────────────────────────────────────────
//   1. Inactive   tariff.is_active === 0. A soft-deleted tariff's
//                 billability is moot; nothing else is worth computing.
//   2. Gap        the chain has a hole (or, rarer, an overlap). This wins
//                 over Expired/Expiring DELIBERATELY: a gap can sit
//                 entirely in the past, or hide behind CURRENT coverage —
//                 test321 bills fine today; its 95-day 2026 hole is
//                 invisible unless something says so. Expired/Expiring
//                 already have a safety net elsewhere (TV-3's Create Bill
//                 Run guard blocks a run that would hit them; the
//                 per-version rows show them too). A gap that nobody is
//                 currently trying to bill through has NO other surface —
//                 this pill is it. Beau, verbatim: "indicating the gap on
//                 the tariff directly so it can immediately be rectified or
//                 acknowledged."
//   3. Expired    nothing covers today. Blocks billing NOW.
//   4. Expiring   in force, but within tariffEffectivity's warning window.
//   5. Active     the common case. MUST render byte-identical to the
//                 pre-existing badge (`tone="success"`, text `"Active"`) —
//                 the whole point is that ten healthy tariffs gain no
//                 noise.
//
// `scheduled` and `none` (tariffEffectivity states meaning "nothing has
// EVER been in force yet") both read as "Expired" here. The question this
// pill answers is binary — can it bill today — and both answer "no" the
// same way `expired` does. The finer distinction stays exactly where it
// already lived: the per-version rows (`versionEffectivityLabel`), which
// this file does not touch. "The rolled-up pill answers 'does this tariff
// need attention?'; the version rows answer 'which version do I edit?'"
//
// GAP WORDING — decided and stated: the card shows a DAY COUNT ("Gap: 95
// days"), not the exact span. `chainIntegrityBadgeLabel` (tariffChainIntegrity.ts)
// already proved that shape fits the ~341px mobile gutter budget (shorter
// than "No version in effect", the previous widest card badge). Keeping
// every state to a bounded "word: count" shape is what keeps the pill the
// same rough width in every state — "Expires in N days" already set that
// precedent; Gap now matches it rather than introducing a longer one. The
// EXACT span stays one click away, in the expanded panel's
// `chainIntegrityDetailLabel` (renderChainIntegrityDetail in
// TariffManagement.tsx) — unchanged by this file.
//
// GRACEFUL DEGRADATION: `versions` is `undefined` before the per-tariff
// version sweep lands, or if it failed — the sweep is a best-effort
// decoration (TariffManagement.tsx's own `sweepEffectivity` comment), and a
// failed fetch must not paint a WRONG pill. `undefined` falls back to the
// plain `tariff.is_active`-only reading (Active/Inactive), exactly what the
// card showed before this file existed — never "no badge" (this slot was
// never absent) and never a state this file cannot actually back with data.
// ---------------------------------------------------------------------------

import type { BadgeTone } from '../components/ui';
import { summariseTariff, tariffEffectivityLabel, type EffectiveWindow } from './tariffEffectivity';
import { summariseChainIntegrity, chainIntegrityBadgeLabel, type ChainWindow } from './tariffChainIntegrity';

/** The only fields the pill reads from a version row — the union of what both composed modules need. */
export interface TariffVersionRow extends EffectiveWindow, ChainWindow {
  id: string;
}

export type TariffStatusPillState = 'inactive' | 'gap' | 'expired' | 'expiring' | 'active';

export interface TariffStatusPill {
  state: TariffStatusPillState;
  label: string;
  tone: BadgeTone;
}

const TONE: Record<TariffStatusPillState, BadgeTone> = {
  inactive: 'danger',
  gap: 'danger',
  expired: 'danger',
  expiring: 'warning',
  active: 'success',
};

/** Is the TARIFF row itself active? Same permissive reading as every `is_active` flag in this codebase. */
function isTariffActive(flag: number | boolean | null | undefined): boolean {
  if (flag === undefined || flag === null) return true;
  return flag !== 0 && flag !== false;
}

/**
 * The one pill for one tariff card.
 *
 * `versions` is `undefined` when the per-tariff sweep has not landed yet or
 * failed — see the module header's "GRACEFUL DEGRADATION". When present, it
 * is every version row for this ONE tariff (any order, active or not — same
 * contract as every function it delegates to).
 */
export function tariffStatusPill(
  tariffIsActiveFlag: number | boolean | null | undefined,
  versions: readonly TariffVersionRow[] | undefined,
  todayKey: string,
): TariffStatusPill {
  if (!isTariffActive(tariffIsActiveFlag)) {
    return { state: 'inactive', label: 'Inactive', tone: TONE.inactive };
  }

  if (versions === undefined) {
    return { state: 'active', label: 'Active', tone: TONE.active };
  }

  const chain = summariseChainIntegrity(versions);
  if (chain.hasIssue) {
    return { state: 'gap', label: chainIntegrityBadgeLabel(chain) ?? 'Gap', tone: TONE.gap };
  }

  const effectivity = summariseTariff(versions, todayKey);
  switch (effectivity.state) {
    case 'expired':
    case 'scheduled':
    case 'none':
      return { state: 'expired', label: 'Expired', tone: TONE.expired };
    case 'expiring':
      return { state: 'expiring', label: tariffEffectivityLabel(effectivity) ?? 'Expiring', tone: TONE.expiring };
    case 'ok':
      return { state: 'active', label: 'Active', tone: TONE.active };
  }
}
